import { TFunction } from 'i18next'
import { boolean, date, mixed, number, object, SchemaOf, string } from 'yup'

import { AccountingBasis } from '@modules-deprecated/app/organization/types/accountingBasis'
import { Organization } from '@modules-deprecated/app/organization/types/organization'
import { OrganizationSettings } from '@modules-deprecated/app/organization/types/organizationSettings'

import { ReconciliationView } from '../enums/reconciliationView'
import { TaxationType } from '../enums/taxationType'
import { convertDuplicateBillCheckToBoolean } from './convertDuplicateBillCheck'
import { convertSupplierInvoiceNoToBoolean } from './convertSupplierInvoiceNo'
import { getDate, getFiscalYearEnd } from './formHelpers'

export interface DefaultValueOptions {
  shouldShowBankSyncSettings?: boolean
}

export interface OrganizationAccountingForm {
  accountingBasis: AccountingBasis | undefined
  billEmailAddress: string
  defaultBankFeeAccountId: string | undefined
  defaultBillBankAccountId: string
  firstFiscalYearEnd: number
  firstFiscalYearStart: Date | undefined
  fiscalLockingDate: Date | undefined | null
  fiscalYearEndMonth: number
  hasBillVoucherNo: boolean | undefined
  isDuplicateBillCheckDisabled: boolean | undefined
  isGermanOrganization: boolean | undefined
  reconciliationView: ReconciliationView
  salesTaxPeriod: string
  shouldShowBankSyncSettings: boolean | undefined
  supplierInvoiceNoEnabled: boolean | undefined
  taxationType: TaxationType | null | undefined
}

export type OrganizationAccountingFormSchema = SchemaOf<OrganizationAccountingForm>

export const getValidationSchema = (t: TFunction): OrganizationAccountingFormSchema =>
  object({
    accountingBasis: mixed().oneOf(Object.values(AccountingBasis)),
    billEmailAddress: string().required(),
    defaultBankFeeAccountId: string().when('shouldSeeBankSyncSettings', (shouldSeeBankSyncSettings, schema) =>
      shouldSeeBankSyncSettings ? schema.required() : schema,
    ),
    defaultBillBankAccountId: string().required(),
    firstFiscalYearEnd: number().required(),
    firstFiscalYearStart: date().nullable().required(t('required_field')),
    fiscalLockingDate: date().nullable(),
    fiscalYearEndMonth: number().required(),
    hasBillVoucherNo: boolean().required(),
    isDuplicateBillCheckDisabled: boolean().required(),
    isGermanOrganization: boolean(),
    reconciliationView: mixed().oneOf(Object.values(ReconciliationView)).required(),
    salesTaxPeriod: string().required(),
    shouldShowBankSyncSettings: boolean(),
    supplierInvoiceNoEnabled: boolean().required(),
    taxationType: mixed().when('isGermanOrganization', {
      is: true,
      then: mixed().oneOf(Object.values(TaxationType)).required(),
      otherwise: mixed().nullable(),
    }),
  })

export const getDefaultValues = (
  organization?: Organization,
  organizationSettings?: OrganizationSettings,
  options?: DefaultValueOptions,
  taxationType?: TaxationType,
): OrganizationAccountingForm => ({
  accountingBasis: organization?.accountingBasis,
  billEmailAddress: organization?.billEmailAddress || '',
  defaultBankFeeAccountId: organization?.defaultBankFeeAccountId || undefined,
  defaultBillBankAccountId: organization?.defaultBillBankAccountId || '',
  firstFiscalYearEnd: getFiscalYearEnd(organization?.firstFiscalYearEnd),
  firstFiscalYearStart: getDate(organization?.firstFiscalYearStart),
  fiscalLockingDate: getDate(organization?.fiscalLockingDate) ?? null,
  // BE has numbers 1-12 for months, need to convert to 0-11 range
  fiscalYearEndMonth: organization?.fiscalYearEndMonth ? organization.fiscalYearEndMonth - 1 : 0,
  hasBillVoucherNo: organization?.hasBillVoucherNo,
  isDuplicateBillCheckDisabled: convertDuplicateBillCheckToBoolean(organizationSettings?.isDuplicateBillCheckDisabled),
  isGermanOrganization: organization?.countryId === 'DE',
  reconciliationView:
    organizationSettings?.isLegacyReconciliationViewEnabled === '1'
      ? ReconciliationView.Classic
      : ReconciliationView.Transactions,
  salesTaxPeriod: organization?.salesTaxPeriod || 'month',
  shouldShowBankSyncSettings: options?.shouldShowBankSyncSettings,
  supplierInvoiceNoEnabled: convertSupplierInvoiceNoToBoolean(organizationSettings?.supplierInvoiceNoEnabled),
  taxationType: taxationType ?? (organization?.countryId === 'DE' ? TaxationType.OnPayment : null),
})
